/**
 * Sync Logger — structured diagnostic logs persisted to IndexedDB.
 *
 * Why: when SolidHome (or anyone) reports "sync just doesn't work," we want
 * forensic evidence — not just console logs that disappear on page reload.
 *
 * Logs are capped at 500 entries; oldest are pruned on insert. Visible in the
 * Sync Queue Recovery page and included in the Export Diagnostic JSON dump.
 *
 * Levels:
 *   - debug : verbose, dev-only by default
 *   - info  : normal lifecycle (queued, uploaded, synced)
 *   - warn  : deferred / will retry
 *   - error : marked failed, gave up
 */

import { put, getAll, remove, STORES } from './offlineDB';

const MAX_LOGS = 500;

let logFlushing = false;

export function logSync(level, event, data = {}) {
  // Always console.log for live debugging
  const fn = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
  fn(`[Sync:${level}] ${event}`, data);
  // Persist async without blocking
  put(STORES.SYNC_LOGS, {
    timestamp: new Date().toISOString(),
    level,
    event,
    data,
  }).catch(() => { /* never throw from logger */ });

  // Periodic pruning (debounced)
  if (!logFlushing) {
    logFlushing = true;
    setTimeout(() => {
      pruneLogs().finally(() => { logFlushing = false; });
    }, 2000);
  }
}

async function pruneLogs() {
  try {
    const all = await getAll(STORES.SYNC_LOGS);
    if (all.length <= MAX_LOGS) return;
    // Sort by timestamp ascending, remove oldest
    all.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const toRemove = all.slice(0, all.length - MAX_LOGS);
    for (const l of toRemove) await remove(STORES.SYNC_LOGS, l.id);
  } catch {
    // swallow
  }
}

export async function getRecentLogs(limit = 100) {
  const all = await getAll(STORES.SYNC_LOGS);
  return all.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, limit);
}

export async function clearLogs() {
  const all = await getAll(STORES.SYNC_LOGS);
  for (const l of all) await remove(STORES.SYNC_LOGS, l.id);
}
